'use client';

import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useRef } from 'react';

import { Button } from '@/components/ui/Button';
import V12Logo from '@/components/ui/icons/payment/svg/v12.svg';
import { computeV12PdpTeaserPayments, isV12Available } from '@/lib/checkout/payments/methods/v12';
import { formatPrice } from '@/lib/utils/formatPrice';
import { modalActions } from '@/states/modalState';

import styles from './V12Sentence.module.scss';

export interface V12SentenceProps {
  amount: number;
  currency: string;
  countryCode: string;
  ctaType: string;
  v12PaymentMin?: number;
  v12PaymentMax?: number;
  ctaLabel: string;
  onAddToCart: () => void | Promise<void>;
  className?: string;
}

/**
 * UK V12 Retail Finance teaser on PDP — “from £X /month” + link to the loan calculator modal.
 * Visibility matches legacy Breitling: GB, ORDER CTA, amount within market-config limits.
 */
export function V12Sentence({
  amount,
  currency,
  countryCode,
  ctaType,
  v12PaymentMin,
  v12PaymentMax,
  ctaLabel,
  onAddToCart,
  className,
}: Readonly<V12SentenceProps>) {
  const t = useTranslations('product.v12');
  const locale = useLocale();
  // Keep modal CTA on the latest handler — valtio stores the open-time function.
  const onAddToCartRef = useRef(onAddToCart);
  onAddToCartRef.current = onAddToCart;

  const payments = useMemo(() => computeV12PdpTeaserPayments(amount), [amount]);

  if (!payments || !isV12Available(amount, countryCode, ctaType, v12PaymentMin, v12PaymentMax)) {
    return null;
  }

  const monthlyAmount = formatPrice(Math.ceil(Number(payments.initialPayments)), currency, locale);

  const openCalculator = () => {
    modalActions.open('v12-loan-calculator', {
      amount,
      currency,
      locale,
      ctaLabel,
      onAddToCart: () => onAddToCartRef.current(),
    });
  };

  return (
    <div className={className} data-testid="V12Sentence">
      <p className={styles.prefix} data-testid="V12InstallmentAmountText">
        {t('installmentInfoPrefix', { amount: monthlyAmount })}
        &nbsp;
        <span className={clsx(styles.withWord, styles.withWordMobile)}>
          {t('installmentInfoWith')}
        </span>
      </p>
      <div className={styles.actions} data-testid="InstallmentInfoSection">
        <span className={clsx(styles.withWord, styles.withWordDesktop)}>
          {t('installmentInfoWith')}
        </span>
        <button
          type="button"
          className={styles.logoButton}
          onClick={openCalculator}
          aria-label={t('openCalculatorAria')}
          data-testid="V12AvailableImage"
        >
          <V12Logo className={styles.logo} aria-hidden />
        </button>
        <Button
          type="button"
          variant="link"
          className={styles.viewOptions}
          onClick={openCalculator}
          data-testid="V12ViewOptionsButton"
        >
          {t('installmentInfoViewOptions')}
        </Button>
      </div>
    </div>
  );
}

V12Sentence.displayName = 'V12Sentence';
